import { useEffect, useId, useRef, useState } from 'react';
import { Box, CircularProgress, Dialog } from '@mui/material';

import { BRAND } from '../../shared/theme/paletteDark';
import { useIsMobile } from '../../shared/useIsMobile';
import { shareUrl, type ScheduleLink } from '../account/accountApi';
import { useMe } from '../account/meContext';
import {
  CloseButton,
  Confirm,
  DialogHeading,
  DialogText,
  ErrorText,
  PrimaryAction,
  SecondaryAction,
  SecondaryLink,
  SmallPrint,
  Stack,
  TextAction,
  UrlBox,
  dialogPaperSx,
} from './dialogParts';
import { useCopy } from './useCopy';

/**
 * Share My Schedule → "Share a public link" (D11).
 *
 * Off until the owner makes one; then it can be copied (or handed to the
 * phone's own share sheet), replaced, or turned off. What a viewer sees is
 * said once, plainly, above everything else.
 */

type Busy = 'create' | 'rotate' | 'delete' | null;

export default function ShareLinkDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { api } = useMe();
  const mobile = useIsMobile();
  const headingId = useId();
  /** Undefined while asking, null when there is none. */
  const [link, setLink] = useState<ScheduleLink | null | undefined>(undefined);
  const [loadFailed, setLoadFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState<Busy>(null);
  const [failed, setFailed] = useState('');
  const [confirm, setConfirm] = useState<'rotate' | 'delete' | null>(null);
  const [notice, setNotice] = useState('');
  const field = useRef<HTMLInputElement | null>(null);
  const { copy, copied, manual } = useCopy(() => field.current);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLink(undefined);
    setLoadFailed(false);
    setFailed('');
    setConfirm(null);
    setNotice('');
    api
      .links()
      .then((links) => {
        if (!cancelled) setLink(links.share);
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open, api, attempt]);

  const run = async (which: Exclude<Busy, null>, action: () => Promise<void>, done: string) => {
    setBusy(which);
    setFailed('');
    try {
      await action();
      setConfirm(null);
      setNotice(done);
    } catch {
      setFailed('That didn’t work. Check your connection and try again.');
    } finally {
      setBusy(null);
    }
  };

  const create = () =>
    run('create', async () => setLink(await api.createLink('share')), '');
  const rotate = () =>
    run('rotate', async () => setLink(await api.rotateLink('share')), 'New link ready. The old one no longer works.');
  const remove = () =>
    run(
      'delete',
      async () => {
        await api.deleteLink('share');
        setLink(null);
      },
      'Link turned off.',
    );

  const url = link ? shareUrl(link.token) : '';
  // The phone's own share sheet (Messages, WhatsApp…) where there is one.
  const canShare = mobile && typeof navigator.share === 'function';

  const shareNatively = async () => {
    try {
      await navigator.share({ title: 'My Climate Week schedule', url });
    } catch {
      // Dismissed, or refused: the Copy button is right there.
    }
  };

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby={headingId} slotProps={{ paper: { sx: dialogPaperSx } }}>
      <CloseButton type="button" aria-label="Close" onClick={onClose}>
        ×
      </CloseButton>
      <DialogHeading id={headingId}>Share a public link</DialogHeading>
      <DialogText>
        Anyone with the link sees your first name and your scheduled events, with personal events shown only as
        “Busy” — never your Save for Later list, your email or your sign-up answers.
      </DialogText>

      {loadFailed && (
        <ErrorText role="alert">
          We couldn’t load your link.{' '}
          <TextAction type="button" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </TextAction>
        </ErrorText>
      )}
      {failed && <ErrorText role="alert">{failed}</ErrorText>}

      {link === undefined && !loadFailed && (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 2 }} role="status" aria-label="Loading">
          <CircularProgress size={26} sx={{ color: BRAND.parchment }} />
        </Box>
      )}

      {link === null && (
        <PrimaryAction type="button" onClick={create} disabled={busy !== null} aria-busy={busy === 'create'}>
          {busy === 'create' ? 'Creating…' : 'Create link'}
        </PrimaryAction>
      )}

      {link && (
        <>
          <UrlBox
            ref={field}
            readOnly
            value={url}
            aria-label="Your public link"
            onFocus={(e) => e.currentTarget.select()}
          />
          <Stack>
            {canShare && (
              <PrimaryAction type="button" onClick={shareNatively}>
                Share…
              </PrimaryAction>
            )}
            {canShare ? (
              <SecondaryAction type="button" onClick={() => copy(url)}>
                {copied ? 'Copied!' : 'Copy link'}
              </SecondaryAction>
            ) : (
              <PrimaryAction type="button" onClick={() => copy(url)}>
                {copied ? 'Copied!' : 'Copy link'}
              </PrimaryAction>
            )}
            <SecondaryLink href={url} target="_blank" rel="noopener noreferrer">
              See what they see
            </SecondaryLink>
          </Stack>
          {manual && <SmallPrint role="status">Selected: press ⌘C, or Ctrl+C, to copy it.</SmallPrint>}

          {confirm ? (
            <Confirm>
              {confirm === 'rotate'
                ? 'Replace this link? The current one stops working, so anyone you’ve sent it to will need the new one.'
                : 'Turn off this link? Anyone who opens it will be told it’s been turned off. You can make a new one any time.'}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.25 }}>
                <SecondaryAction
                  type="button"
                  style={{ width: 'auto', minHeight: 38 }}
                  disabled={busy !== null}
                  aria-busy={busy !== null}
                  onClick={confirm === 'rotate' ? rotate : remove}
                >
                  {busy
                    ? 'Working…'
                    : confirm === 'rotate'
                      ? 'Replace link'
                      : 'Turn off link'}
                </SecondaryAction>
                <TextAction type="button" onClick={() => setConfirm(null)} disabled={busy !== null}>
                  Cancel
                </TextAction>
              </Box>
            </Confirm>
          ) : (
            <Box sx={{ display: 'flex', gap: 2.5, mt: 1.5 }}>
              <TextAction type="button" onClick={() => setConfirm('rotate')}>
                Replace link
              </TextAction>
              <TextAction type="button" onClick={() => setConfirm('delete')}>
                Turn off link
              </TextAction>
            </Box>
          )}
        </>
      )}

      {notice && <SmallPrint role="status">{notice}</SmallPrint>}
    </Dialog>
  );
}
